import { useCallback, useState } from "react";
import { toast } from "sonner";
import useSWR from "swr";
import {
	type BillingInterval,
	type BillingSummary,
	type CreditPack,
	changePlan,
	getBillingCatalog,
	getBillingSummary,
	openPortal,
	type Plan,
	type PortalFlow,
	startCheckout,
	startCreditCheckout,
} from "@/api/billing";
import { useAuth } from "@/auth/auth-provider";
import { ApiError } from "@/lib/api-client";
import { useRecord, useRecords } from "@/sync/hooks";

/**
 * Billing summary (plan, limits, credits). Revalidates whenever the synced balance, subscription
 * or workspace plan changes, so webhooks and usage show up live without polling.
 */
export function useBillingSummary() {
	const { workspace } = useAuth();
	const balance = useRecord("creditBalance", workspace?.id);
	const plan = useRecord("workspace", workspace?.id)?.planId;
	const subscription = useRecords("subscription")[0];
	const version = `${balance?.updatedAt}|${plan}|${subscription?.updatedAt}`;
	return useSWR(
		workspace ? ["/billing/summary", workspace.id, version] : null,
		() => getBillingSummary(),
		{ keepPreviousData: true, revalidateOnFocus: true, focusThrottleInterval: 30_000 },
	);
}

export function useBillingCatalog() {
	return useSWR("/billing/catalog", getBillingCatalog, {
		dedupingInterval: 5 * 60_000,
		revalidateOnFocus: false,
	});
}

export type CreditLevel = "ok" | "low" | "empty" | "past_due";

/** Live credit state: the synced balance renders instantly; plan context comes from the summary. */
export function useCredits() {
	const { workspace } = useAuth();
	const record = useRecord("creditBalance", workspace?.id);
	const { data: summary } = useBillingSummary();
	const total = record?.balanceMillicredits ?? summary?.credits.balanceMillicredits ?? null;
	const included = record?.includedMillicredits ?? summary?.credits.includedMillicredits ?? 0;
	const pastDue = summary?.credits.paymentPastDue ?? false;
	const spendable = total == null ? null : pastDue ? total - included : total;
	const threshold = summary?.credits.lowBalanceThresholdMillicredits ?? 20_000;
	const level: CreditLevel =
		spendable == null
			? "ok"
			: pastDue
				? "past_due"
				: spendable <= 0
					? "empty"
					: spendable < threshold
						? "low"
						: "ok";
	return {
		total,
		included,
		pack: total == null ? 0 : Math.max(0, total - included),
		spendable,
		threshold,
		allowance: summary?.plan?.includedMillicredits ?? null,
		level,
		summary,
	};
}

/** Plan + pack actions shared by the billing page and the global dialog. */
export function useBillingActions(summary: BillingSummary | undefined) {
	const { pending, run } = useRedirectAction();
	const subscribed = !!summary?.subscription?.managedByStripe;
	const choosePlan = (plan: Plan, interval: BillingInterval) => {
		if (plan.monthlyPriceCents === 0) {
			if (subscribed) void run(plan.id, () => openPortal("subscription_cancel"));
			return;
		}
		void run(plan.id, () =>
			subscribed
				? changePlan({ planId: plan.id, interval })
				: startCheckout({ planId: plan.id, interval }),
		);
	};
	const buyPack = (pack: CreditPack, saveCard: boolean) =>
		void run(pack.id, () => startCreditCheckout({ packId: pack.id, saveCard }));
	const portal = (flow: PortalFlow = "home") => void run(`portal:${flow}`, () => openPortal(flow));
	return { pending, choosePlan, buyPack, portal };
}

/**
 * Runs a billing action that returns a Stripe URL and redirects to it. `pending` holds the key of
 * the running action so the matching button shows its spinner.
 */
export function useRedirectAction() {
	const [pending, setPending] = useState<string | null>(null);
	const run = useCallback(async (key: string, action: () => Promise<{ url: string }>) => {
		setPending(key);
		try {
			const { url } = await action();
			window.location.assign(url);
		} catch (error) {
			setPending(null);
			if (error instanceof ApiError && error.status === 402) return;
			toast.error(
				error instanceof ApiError && error.code === "billing_not_configured"
					? "Billing isn't configured yet"
					: "Couldn't open checkout",
				{ description: error instanceof Error ? error.message : undefined },
			);
		}
	}, []);
	return { pending, run };
}
